import { memo, useMemo } from 'react';
import { Box, Tooltip, IconButton } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { FormFieldComponentProps } from './FormField.types';
import {
  StyledFormFieldContainer,
  StyledFormLabel,
  StyledFormHelperText,
  StyledOptionalText,
  StyledCharacterCount,
} from './FormField.styles';

/**
 * FormField Component
 *
 * Professional form field wrapper component providing consistent label, helper text,
 * and error message display. Follows the green/teal design system.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <FormField label="Email Address" required>
 *   <Input type="email" />
 * </FormField>
 *
 * // With error
 * <FormField
 *   label="Password"
 *   error
 *   errorMessage="Password must be at least 8 characters"
 * >
 *   <Input type="password" />
 * </FormField>
 *
 * // With character count
 * <FormField
 *   label="Bio"
 *   characterCount={{ current: 120, max: 500 }}
 * >
 *   <Input multiline rows={4} />
 * </FormField>
 * ```
 */
export const FormField = memo<FormFieldComponentProps>(
  function FormField({
    label,
    children,
    helperText,
    validationState = 'default',
    error = false,
    errorMessage,
    successMessage,
    warningMessage,
    required = false,
    optional = false,
    fullWidth = true,
    htmlFor,
    className,
    'data-testid': dataTestId,
    infoTooltip,
    characterCount,
  }) {
    // Determine validation state
    const finalValidationState = useMemo(() => {
      if (error || errorMessage) return 'error';
      if (successMessage) return 'success';
      if (warningMessage) return 'warning';
      return validationState;
    }, [error, errorMessage, successMessage, warningMessage, validationState]);

    // Determine helper text
    const finalHelperText = useMemo(() => {
      if (errorMessage) return errorMessage;
      if (successMessage) return successMessage;
      if (warningMessage) return warningMessage;
      return helperText;
    }, [errorMessage, successMessage, warningMessage, helperText]);

    // Character count warning threshold (90% of max)
    const characterCountWarning = useMemo(() => {
      if (!characterCount) return false;
      return characterCount.current > characterCount.max * 0.9;
    }, [characterCount]);

    return (
      <StyledFormFieldContainer
        validationState={finalValidationState}
        className={className}
        data-testid={dataTestId}
        sx={{ width: fullWidth ? '100%' : 'auto' }}
      >
        {label && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StyledFormLabel
                htmlFor={htmlFor}
                required={required}
              >
                {label}
                {optional && (
                  <StyledOptionalText>(optional)</StyledOptionalText>
                )}
              </StyledFormLabel>

              {infoTooltip && (
                <Tooltip title={infoTooltip} arrow>
                  <IconButton size="small" sx={{ ml: 0.5, p: 0.25 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {characterCount && (
              <StyledCharacterCount warning={characterCountWarning}>
                {characterCount.current}/{characterCount.max}
              </StyledCharacterCount>
            )}
          </Box>
        )}

        <Box>{children}</Box>

        {finalHelperText && (
          <StyledFormHelperText error={finalValidationState === 'error'}>
            {finalHelperText}
          </StyledFormHelperText>
        )}
      </StyledFormFieldContainer>
    );
  }
);

FormField.displayName = 'FormField';

export default FormField;
